/**
 * Cart Drawer Upsell Component - "You may also need" carousel
 * Initializes a Swiper with dot pagination and handles AJAX add-to-cart
 * via the theme's cart events, so the drawer re-renders (and this
 * component re-initializes) after each add.
 *
 * Cart page can show 2 products per row; drawer stays at 1.
 */

import { ThemeBus } from './events-bus.js';
import { EVENTS } from './event-constants.js';

const SWIPER_WAIT_INTERVAL_MS = 100;
const SWIPER_WAIT_MAX_TRIES = 50;
const TWO_COL_BREAKPOINT_PX = 1025;

class CartUpsellComponent extends HTMLElement {
  connectedCallback() {
    this.swiper = null;
    this.sliderEl = this.querySelector('.cart-upsell-swiper');

    this.querySelectorAll('[data-cart-upsell-form]').forEach((form) => {
      form.addEventListener('submit', this.#handleSubmit);
    });

    this.#initSliderWhenReady();
  }

  disconnectedCallback() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  }

  /**
   * Swiper is loaded globally as a deferred module, so it may not be
   * available yet when the drawer (re-)renders. Retry briefly until it is.
   */
  #initSliderWhenReady(tries = 0) {
    if (!this.sliderEl || this.swiper) return;

    if (window.Swiper) {
      this.#initSlider();
      return;
    }

    if (tries >= SWIPER_WAIT_MAX_TRIES) return;
    setTimeout(() => this.#initSliderWhenReady(tries + 1), SWIPER_WAIT_INTERVAL_MS);
  }

  #initSlider() {
    const slideCount = this.sliderEl.querySelectorAll('.swiper-slide').length;
    const paginationEl = this.querySelector('.cart-upsell-pagination');
    const columns = Math.max(1, Number(this.dataset.columns) || 1);

    const options = {
      slidesPerView: 1,
      slidesPerGroup: 1,
      spaceBetween: 16,
      grabCursor: true,
      watchOverflow: true,
      observer: true,
      observeParents: true,
    };

    // Cart page: 2-up from md breakpoint; drawer stays single-column
    if (columns > 1) {
      options.breakpoints = {
        [TWO_COL_BREAKPOINT_PX]: {
          slidesPerView: Math.min(columns, slideCount),
          slidesPerGroup: Math.min(columns, slideCount),
          spaceBetween: 16,
        },
      };
    }

    if (slideCount > 1) {
      // Rewind avoids loop clones blowing out flex layouts on /cart
      options.rewind = true;
      options.autoplay = {
        delay: 5000,
        disableOnInteraction: true,
      };

      if (paginationEl) {
        options.pagination = {
          el: paginationEl,
          clickable: true,
        };
      }
    }

    this.swiper = new window.Swiper(this.sliderEl, options);

    // Recalculate after layout settles (cart page flex columns / drawer open)
    requestAnimationFrame(() => {
      if (this.swiper) this.swiper.update();
    });
  }

  #handleSubmit = async (event) => {
    event.preventDefault();

    const form = event.currentTarget;
    const button = form.querySelector('[data-cart-upsell-add]');
    if (button) button.disabled = true;

    ThemeBus.dispatchEvent(new Event(EVENTS.cartUpdating));

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form),
      });
      const result = await response.json();

      if (!response.ok || (result.status && result.status !== 200)) {
        throw new Error(result.message || result.description || 'Add to cart failed');
      }

      // Refresh cached cart data, then let the drawer re-render itself
      const cartResponse = await fetch('/cart.js');
      window.__cart = await cartResponse.json();

      ThemeBus.dispatchEvent(new Event(EVENTS.cartUpdated));
    } catch (error) {
      ThemeBus.dispatchEvent(new Event(EVENTS.cartUpdateEnd));
      if (button) button.disabled = false;
      if (document.body.dataset.environment === 'development') {
        console.error('[CartUpsell] Error adding to cart:', error);
      }
    }
  };
}

if (!customElements.get('cart-upsell-component')) {
  customElements.define('cart-upsell-component', CartUpsellComponent);
}
